import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';
import FILTER_CARD_STATES from '@/components/base_components/tables/filter/FILTER_CARD_STATES';
import tableService from "@/components/base_components/tables/services/table-service";
import convertSearchHeadersMixin from "@/mixins/convert-search-headers-mixin";
import { useTimerManager } from '@/composables/timer-manager';
import { mapActions } from 'vuex';
import { mergeInOrder } from '@/services/sort-service';

export default function (cacheGettersAndSettersMixin) {
  return {
    mixins: [cacheGettersAndSettersMixin, convertSearchHeadersMixin],
    props: {
      initialInvisibleFilters: {
        type: Array, default() {
          return []
        }
      }
    },
    data() {
      return {
        dirtyDialogIsOpen: false,
      }
    },
    setup() {
      const { registerTimeout } = useTimerManager();
      return {
        registerTimeout,
      };
    },
    computed: {
      filterCardState: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.FILTER_CARD_STATE] ?? '';
        },
        async set(state) {
          if (state === FILTER_CARD_STATES.NEW) {
            await this.setCachedPresetObj({});
          }
          this.setCachedFilterCardState(state);
        }
      },
      notVisibleHeaders: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS] ?? [];
        },
        set(notVisibleHeaders) {
          this.setCachedNotVisibleHeaders(notVisibleHeaders, this.tableIdentifier);
        }
      },
      notVisibleFilters: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS] ?? this.initialInvisibleFilters;
        },
        set(notVisibleFilters) {
          this.setCachedNotVisibleFilters(notVisibleFilters);
        }
      },
      orderdFilters: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.FILTER_ORDER] ?? [];
        },
        set(order) {
          this.setCachedFilterOrder(order);
        }
      },
      orderdHeaders: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.HEADER_ORDER] ?? [];
        },
        set(order) {
          if (this.tableNotCachedYet()) { return }
          this.setCachedHeaderOrder(order, this.tableIdentifier);
        }
      },
      preset: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.PRESET] ?? {};
        },
        async set(preset) {
          if (preset == undefined) { await this.resetCachedTable(this.tableIdentifier); }
          this.setCachedExistingPreset(preset, this.tableIdentifier);
        }
      },
      presetIsDefault: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.PRESET]?.isDefault ?? false;
        },
        set(presetIsDefault) {
          this.setCachedPresetIsDefault(presetIsDefault);
        }
      },
      presetName: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.PRESET]?.name ?? '';
        },
        set(name) {
          this.setCachedPresetName(name);
        }
      },
      presetSaveColumns: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.PRESET]?.saveColumns ?? false;
        },
        set(saveColumns) {
          this.setCachedPresetColumnSave(saveColumns);
        }
      },
      truncateData: {
        get() {
          return this.cachedTable?.[TABLE_DATA_KEYS.TRUNCATE_DATA] ?? false;
        },
        set(truncateData) {
          this.setCachedTruncateData(truncateData, this.tableIdentifier);
        }
      },
      truncateOptions: {
        get() {
          return tableService.generateTruncateOptions(this.headers, this.cachedTable?.[TABLE_DATA_KEYS.TRUNCATE_OPTIONS]) ?? {};
        },
        set(truncateOptions) {
          this.setCachedTruncateOptions(truncateOptions, this.tableIdentifier);
        }
      },
      cachedHeaders() {
        return this.cachedTable?.[TABLE_DATA_KEYS.HEADERS] ?? [];
      },
      filters() {
        return this.customFilters ?? this.cachedTable?.[TABLE_DATA_KEYS.FILTERS] ?? {};
      },
      search: {
        get() {
          const searchExpression = this.cachedTable?.[TABLE_DATA_KEYS.SEARCH_EXPRESSION] ?? '';
          const searchHeaders = this.internalHeaders ?? this.headers;
          return this.convertSearchHeaders(searchExpression, searchHeaders);
        },
        set(searchExpression) {
          this.setSearchExpression({
            search: searchExpression,
            $route: this.$route,
          });
        }
      },
      sortBy() {
        return this.cachedTable?.[TABLE_DATA_KEYS.SORT_BY] ?? [];
      },
      sortDesc() {
        return this.cachedTable?.[TABLE_DATA_KEYS.SORT_BY_DESC] ?? [];
      }
    },
    watch: {
      search: {
        handler(newValue, oldValue) {
          if (this.value.length > 0 && newValue !== oldValue) {
            if (!this.dirtyDialogIsOpen) {
              this.dirtyDialogIsOpen = true;
              this.$root.$questionDialog.open({
                message: this.$t('Dialogs.SelectionDirtyDialog.message'),
                title: this.$t('Dialogs.SelectionDirtyDialog.title')
              }).then(async (shouldReset) => {
                if (shouldReset) {
                  this.search = oldValue;
                  await this.$nextTick();
                  this.dirtyDialogIsOpen = false;
                } else {
                  this.dirtyDialogIsOpen = false;
                  await this.$nextTick();
                  this.reloadTable();
                }
              })
            }
          }
        },
        deep: true,
      },
      internalHeaders: {
        handler(newHeaders) {
          this.orderdHeaders = mergeInOrder(
            newHeaders?.map((header) => header.value),
            this.orderdHeaders
          );
        },
        deep: true,
      }
    },
    methods: {
      ...mapActions('tableStore', ['setSearchExpression']),
      invokeSetCachedSort($event) {
        if (this.tableNotCachedYet()) { return }
        this.setCachedSort({ ...$event, subTableIdentifier: this.tableIdentifier });
      },
      invokeSetCachedFilterValue({ key, value }) {
        this.setCachedFilterValue(key, value);
      },
      invokeSetCachedSearch(value) {
        this.setCachedSearchValue(value);
      },
      getHiddenHeaders() {
        const isHiddenByDefault = (header) => header?.isHiddenByDefault;
        const byValue = (header) => header.value;
        return this.headers?.filter(isHiddenByDefault)?.map(byValue) ?? [];
      },
      handleFilterChange(val) {
        if (this.value.length > 0) {
          if (!this.dirtyDialogIsOpen) {
            this.dirtyDialogIsOpen = true;
            this.$root.$questionDialog.open({
              message: this.$t('Dialogs.SelectionDirtyDialog.message'),
              title: this.$t('Dialogs.SelectionDirtyDialog.title')
            }).then(async (shouldReset) => {
              if (shouldReset) {
                this.invokeSetCachedFilterValue({ 'key': val.key, 'value': this.filters[val.key] });
                this.$refs.baseLevelFilterContainer.componentKey += 1;
                await this.$nextTick();
                this.dirtyDialogIsOpen = false;
              } else {
                this.invokeSetCachedFilterValue(val);
                this.dirtyDialogIsOpen = false;
                await this.$nextTick();
                this.reloadTable();
              }
            })
          }
        }
        else {
          this.invokeSetCachedFilterValue(val);
        }
      },
    }
  }
}
